import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AgeBand, SanitizedQuestionItem, SubmitAnswerPayload, TestProgress } from "../../shared/types";
import { trackAnalyticsEvent } from "../lib/analyticsClient";
import { startTest, submitAnswer } from "../lib/testApi";

type TestState = "intro" | "loading" | "active" | "submitting" | "complete" | "error";

export function useTestAttempt() {
  const navigate = useNavigate();
  const [state, setState] = useState<TestState>("intro");
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [question, setQuestion] = useState<SanitizedQuestionItem | null>(null);
  const [progress, setProgress] = useState<TestProgress | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [globalElapsedMs, setGlobalElapsedMs] = useState(0);
  const [questionElapsedMs, setQuestionElapsedMs] = useState(0);
  const questionStartedAt = useRef<number>(0);
  const testStartedAt = useRef<number>(0);
  const firstActionAt = useRef<number | null>(null);
  const changedAnswer = useRef(false);

  const resetQuestionTiming = useCallback(() => {
    questionStartedAt.current = performance.now();
    firstActionAt.current = null;
    changedAnswer.current = false;
    setQuestionElapsedMs(0);
    setSelectedOption(null);
  }, []);

  const begin = useCallback(async (ageBand: AgeBand) => {
    setState("loading");
    setError(null);
    try {
      const response = await startTest(ageBand);
      testStartedAt.current = performance.now();
      setAttemptId(response.attemptId);
      setQuestion(response.currentQuestion);
      setProgress(response.progress);
      void trackAnalyticsEvent("test_started", { attemptId: response.attemptId }, { source: "test_flow" });
      resetQuestionTiming();
      setState("active");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Δεν μπορέσαμε να ξεκινήσουμε το test.");
      setState("error");
    }
  }, [resetQuestionTiming]);

  const selectOption = useCallback((optionId: string) => {
    if (firstActionAt.current === null) {
      firstActionAt.current = performance.now();
    }
    setSelectedOption((current) => {
      if (current && current !== optionId) changedAnswer.current = true;
      return optionId;
    });
  }, []);

  const continueTest = useCallback(async () => {
    if (!attemptId || !question || !selectedOption || state === "submitting") return;

    const now = performance.now();
    const payload: SubmitAnswerPayload = {
      questionId: question.id,
      selectedOption,
      responseTimeMs: Math.round(now - questionStartedAt.current),
      firstActionMs: Math.round((firstActionAt.current ?? now) - questionStartedAt.current),
      changedAnswer: changedAnswer.current
    };

    setState("submitting");
    setError(null);
    try {
      const response = await submitAnswer(attemptId, payload);
      if (response.complete) {
        void trackAnalyticsEvent("test_completed", { attemptId }, { source: "test_flow" });
        setState("complete");
        navigate(response.resultUrl);
        return;
      }

      setQuestion(response.nextQuestion);
      setProgress(response.progress);
      resetQuestionTiming();
      setState("active");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Δεν μπορέσαμε να συνεχίσουμε το test.");
      setState("error");
    }
  }, [attemptId, navigate, question, resetQuestionTiming, selectedOption, state]);

  useEffect(() => {
    if (state !== "active" && state !== "submitting") return;
    const id = window.setInterval(() => {
      const now = performance.now();
      setGlobalElapsedMs(Math.max(0, now - testStartedAt.current));
      setQuestionElapsedMs(Math.max(0, now - questionStartedAt.current));
    }, 500);
    return () => window.clearInterval(id);
  }, [state]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (state !== "active") return;
      if (["1", "2", "3", "4"].includes(event.key)) {
        const option = question?.options[Number(event.key) - 1];
        if (option) selectOption(option.id);
      }
      if (event.key === "Enter" && selectedOption) {
        void continueTest();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [continueTest, question?.options, selectOption, selectedOption, state]);

  const timing = useMemo(
    () => ({ globalElapsedMs, questionElapsedMs, maxTimeSec: question?.maxTimeSec ?? 0 }),
    [globalElapsedMs, questionElapsedMs, question?.maxTimeSec]
  );

  return {
    state,
    attemptId,
    question,
    progress,
    selectedOption,
    error,
    timing,
    begin,
    selectOption,
    continueTest
  };
}
